import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trophy, Users, Target, Zap, Award, Clock, 
  ChevronDown, Star, CheckCircle, HelpCircle,
  Play, ArrowRight, Sparkles, TrendingUp, Smartphone, Globe
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isNativeApp } from '../lib/platform';

// Logo URL
const LOGO_URL = "/logo.jpeg";
// Android app on Google Play
export const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=com.socraquest";
// Inside the iOS/Android app (Capacitor) the download buttons are hidden: users already have the app,
// and App Store guideline 2.3.10 does not allow other mobile platforms to be promoted in an iOS app.
// (The app normally never shows this page: "/" redirects to /login there, see App.js.)

// Android Icon Component
const AndroidIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4483-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4483.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4483.9997-.9993.9997m11.4045-6.02l1.9973-3.4592c.1111-.1924.045-.438-.1476-.5765-.1927-.1111-.4383-.0449-.5765.1476l-2.0216 3.5024c-1.5249-.6928-3.2376-1.0785-5.1332-1.0785-1.8955 0-3.6083.3857-5.1332 1.0785L4.8463 5.4233c-.1382-.1925-.3838-.2587-.5765-.1476-.1926.1385-.2587.3841-.1476.5765l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-9.4396"/>
  </svg>
);

export function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const currentLang = (i18n.language || 'en').split('-')[0];

  const toggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'sk' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePlayNow = () => {
    navigate('/login');
  };

  const showAppDownload = !isNativeApp();
  const handleDownloadAndroid = () => {
    window.open(ANDROID_APP_URL, '_blank');
  };

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const features = [
    { icon: Target, title: t('landing.features.dailyPacks'), desc: t('landing.features.dailyPacksDesc') },
    { icon: Award, title: t('landing.features.bonusUnlock'), desc: t('landing.features.bonusUnlockDesc') },
    { icon: Trophy, title: t('landing.features.globalRankings'), desc: t('landing.features.globalRankingsDesc') },
    { icon: Users, title: t('landing.features.groups'), desc: t('landing.features.groupsDesc') },
    { icon: Star, title: t('landing.features.badges'), desc: t('landing.features.badgesDesc') },
    { icon: Zap, title: t('landing.features.smartScoring'), desc: t('landing.features.smartScoringDesc') }
  ];

  const steps = [
    { step: '01', icon: Target, title: t('landing.howItWorks.step1Title'), desc: t('landing.howItWorks.step1Desc') },
    { step: '02', icon: Award, title: t('landing.howItWorks.step2Title'), desc: t('landing.howItWorks.step2Desc') },
    { step: '03', icon: TrendingUp, title: t('landing.howItWorks.step3Title'), desc: t('landing.howItWorks.step3Desc') }
  ];

  const rewardItems = [
    t('landing.rewards.item1'),
    t('landing.rewards.item2'),
    t('landing.rewards.item3'),
    t('landing.rewards.item4')
  ];

  const prizeList = [
    { rank: '\ud83e\udd47', position: t('landing.rewards.1stPlace'), prize: '$100' },
    { rank: '\ud83e\udd48', position: t('landing.rewards.2ndPlace'), prize: '$50' },
    { rank: '\ud83e\udd49', position: t('landing.rewards.3rdPlace'), prize: '$25' },
    { rank: '\ud83c\udfc5', position: t('landing.rewards.top10'), prize: `$10 ${t('landing.rewards.each')}` }
  ];

  const testimonials = [
    { text: t('landing.testimonials.t1Text'), name: t('landing.testimonials.t1Name'), role: t('landing.testimonials.t1Role') },
    { text: t('landing.testimonials.t2Text'), name: t('landing.testimonials.t2Name'), role: t('landing.testimonials.t2Role') },
    { text: t('landing.testimonials.t3Text'), name: t('landing.testimonials.t3Name'), role: t('landing.testimonials.t3Role') }
  ];

  const faqs = [
    { q: t('landing.faqSection.q1'), a: t('landing.faqSection.a1') },
    { q: t('landing.faqSection.q2'), a: t('landing.faqSection.a2') },
    { q: t('landing.faqSection.q3'), a: t('landing.faqSection.a3') },
    { q: t('landing.faqSection.q4'), a: t('landing.faqSection.a4') },
    { q: t('landing.faqSection.q5'), a: t('landing.faqSection.a5') },
    { q: t('landing.faqSection.q6'), a: t('landing.faqSection.a6') }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] text-white overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-600/5 rounded-full blur-[150px]" />
      </div>

      {/* Sticky Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a1628]/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full shadow-lg shadow-teal-500/30 overflow-hidden border-2 border-teal-400/30">
              <img src={LOGO_URL} alt="SocraQuest Logo" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              SocraQuest
            </span>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('features')} className="text-slate-300 hover:text-teal-400 transition-colors text-sm font-medium">
              {t('landing.nav.features')}
            </button>
            <button onClick={() => scrollToSection('how-it-works')} className="text-slate-300 hover:text-teal-400 transition-colors text-sm font-medium">
              {t('landing.nav.howItWorks')}
            </button>
            <button onClick={() => scrollToSection('rewards')} className="text-slate-300 hover:text-teal-400 transition-colors text-sm font-medium">
              {t('landing.nav.rewards')}
            </button>
            <button onClick={() => scrollToSection('faq')} className="text-slate-300 hover:text-teal-400 transition-colors text-sm font-medium">
              {t('landing.nav.faq')}
            </button>
          </nav>

          {/* Language Switcher + Download App */}
          <div className="flex items-center gap-3">
            <motion.button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-slate-800/60 hover:bg-slate-700/80 text-teal-400 transition-colors border border-slate-600/50 backdrop-blur-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="landing-language-switcher"
              title={currentLang === 'en' ? 'Switch to Slovak' : 'Prepnuť na angličtinu'}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-semibold uppercase">{currentLang === 'en' ? 'EN' : 'SK'}</span>
            </motion.button>

            {showAppDownload && (
            <motion.button
              onClick={handleDownloadAndroid}
              className="relative group px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full font-semibold text-white shadow-lg shadow-green-500/40 hover:shadow-green-500/60 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              animate={{ 
                boxShadow: ['0 10px 40px -10px rgba(34, 197, 94, 0.4)', '0 10px 40px -10px rgba(34, 197, 94, 0.7)', '0 10px 40px -10px rgba(34, 197, 94, 0.4)']
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <AndroidIcon className="w-5 h-5" />
                {t('landing.nav.downloadApp')}
              </span>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity blur-xl" />
            </motion.button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div 
            className="text-center lg:text-left"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Sparkles className="w-4 h-4 text-teal-400" />
              <span className="text-teal-400 text-sm font-medium">{t('landing.hero.badge')}</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
                {t('landing.hero.title1')}
              </span>
              <br />
              <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                {t('landing.hero.title2')}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0">
              {t('landing.hero.subtitlePlain')} <span className="text-teal-400 font-semibold">{t('landing.hero.earnMoney')}</span> {t('landing.hero.subtitleEnd')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
              <motion.button
                onClick={handlePlayNow}
                className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl font-bold text-lg shadow-2xl shadow-teal-500/30 hover:shadow-teal-500/50 transition-all duration-300 overflow-hidden"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Play className="w-5 h-5 fill-current" />
                  {t('landing.hero.startPlaying')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>

              {showAppDownload && (
              <motion.button 
                onClick={handleDownloadAndroid}
                className="relative group px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl font-bold text-lg shadow-xl shadow-green-500/30 hover:shadow-green-500/50 transition-all duration-300 overflow-hidden"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                animate={{ 
                  boxShadow: ['0 20px 50px -12px rgba(34, 197, 94, 0.3)', '0 20px 50px -12px rgba(34, 197, 94, 0.6)', '0 20px 50px -12px rgba(34, 197, 94, 0.3)']
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <AndroidIcon className="w-6 h-6" />
                  {t('landing.hero.downloadAndroid')}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-green-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>
              )}
            </div>

            <div className="flex items-center justify-center lg:justify-start gap-4 mt-4">
              <button 
                onClick={() => scrollToSection('rankings')}
                className="px-6 py-3 border border-slate-600 hover:border-teal-500/50 rounded-xl font-medium text-slate-300 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Trophy className="w-5 h-5 text-teal-400" />
                {t('landing.hero.viewRankings')}
              </button>
            </div>

            <p className="text-sm text-slate-500 mt-4">
              {t('landing.hero.dailyStats')}
            </p>
          </motion.div>

          {/* Right - Phone Mockup */}
          <motion.div 
            className="relative flex justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              <div className="w-[280px] md:w-[320px] h-[580px] md:h-[640px] bg-gradient-to-b from-slate-800 to-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-black/50 border border-slate-700">
                <div className="w-full h-full bg-[#0d1f35] rounded-[2.5rem] overflow-hidden relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-2xl" />
                  <div className="pt-10 px-4 h-full flex flex-col">
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 rounded-full mx-auto mb-2 overflow-hidden shadow-lg shadow-teal-500/30 border-2 border-teal-400/30">
                        <img src={LOGO_URL} alt="SocraQuest" className="w-full h-full object-cover" />
                      </div>
                      <p className="text-teal-400 font-bold">{t('landing.phone.todaysPack')}</p>
                      <p className="text-slate-400 text-sm">{t('landing.phone.quizzesBonus')}</p>
                    </div>

                    <div className="space-y-3 flex-1">
                      {[1, 2, 3, 4].map((i) => (
                        <motion.div 
                          key={i}
                          className={`p-3 rounded-xl ${i === 1 ? 'bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-500/30' : 'bg-slate-800/50'}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + i * 0.1 }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${i === 1 ? 'bg-teal-500' : 'bg-slate-700'}`}>
                                <span className="text-xs font-bold">{i}</span>
                              </div>
                              <div>
                                <p className="text-sm font-medium">{t('home.quiz')} {i}</p>
                                <p className="text-xs text-slate-500">{t('landing.phone.topics')}</p>
                              </div>
                            </div>
                            {i === 1 ? (
                              <span className="text-xs bg-teal-500 px-2 py-1 rounded-full">{t('landing.phone.play')}</span>
                            ) : i < 3 ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-slate-600" />
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="pb-8 pt-4">
                      <div className="bg-slate-800/50 rounded-xl p-3 flex items-center justify-around">
                        <div className="text-center">
                          <p className="text-teal-400 font-bold">2/10</p>
                          <p className="text-xs text-slate-500">{t('landing.phone.complete')}</p>
                        </div>
                        <div className="w-px h-8 bg-slate-700" />
                        <div className="text-center">
                          <p className="text-yellow-400 font-bold">🔒</p>
                          <p className="text-xs text-slate-500">{t('landing.phone.bonus')}</p>
                        </div>
                        <div className="w-px h-8 bg-slate-700" />
                        <div className="text-center">
                          <p className="text-cyan-400 font-bold">#12</p>
                          <p className="text-xs text-slate-500">{t('landing.phone.rank')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <motion.div 
                className="absolute -top-4 -right-4 bg-gradient-to-br from-yellow-400 to-orange-500 px-4 py-2 rounded-xl shadow-lg"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <p className="text-sm font-bold text-white">🏆 {t('landing.phone.winRewards')}</p>
              </motion.div>

              <motion.div 
                className="absolute bottom-20 -left-8 bg-slate-800 border border-slate-700 px-4 py-3 rounded-xl shadow-lg"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                  <div>
                    <p className="text-sm font-medium">Alice</p>
                    <p className="text-xs text-teal-400">98% • 3:10</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-slate-500" />
        </motion.div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.howItWorks.titlePlain')} <span className="text-teal-400">{t('landing.howItWorks.titleHighlight')}</span> {t('landing.howItWorks.titleEnd')}
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {t('landing.howItWorks.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item, i) => (
              <motion.div 
                key={i}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-teal-500/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative p-8 text-center">
                  <div className="text-6xl font-bold text-slate-800 mb-4">{item.step}</div>
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-teal-500/20">
                    <item.icon className="w-8 h-8 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-slate-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="relative py-24 px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.features.title')} <span className="text-teal-400">{t('landing.features.titleHighlight')}</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className="group p-6 bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/50 hover:border-teal-500/30 rounded-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-teal-500/30 group-hover:to-cyan-500/30 transition-all">
                  <feature.icon className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section id="rewards" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 text-sm font-medium">{t('landing.rewards.badge')}</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('landing.rewards.title')} <span className="text-teal-400">{t('landing.rewards.titleHighlight')}</span>
              </h2>
              
              <p className="text-slate-400 text-lg mb-8">
                {t('landing.rewards.subtitle')}
              </p>

              <div className="space-y-4 mb-8">
                {rewardItems.map((item, i) => (
                  <motion.div 
                    key={i} 
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </motion.div>
                ))}
              </div>

              <motion.button
                onClick={handlePlayNow}
                className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl font-bold text-lg shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                {t('landing.rewards.startEarning')}
              </motion.button>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 border border-slate-700">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Trophy className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">{t('landing.rewards.monthlyRewards')}</h3>
                  <p className="text-slate-400">{t('landing.rewards.monthlySubtitle')}</p>
                </div>

                <div className="space-y-4">
                  {prizeList.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{item.rank}</span>
                        <span className="font-medium">{item.position}</span>
                      </div>
                      <span className="text-teal-400 font-bold">{item.prize}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Rankings Preview */}
      <section id="rankings" className="relative py-24 px-6 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.leaderboard.title')} <span className="text-teal-400">{t('landing.leaderboard.titleHighlight')}</span>
            </h2>
            <p className="text-slate-400 text-lg">
              {t('landing.leaderboard.subtitle')}
            </p>
          </motion.div>

          <motion.div 
            className="bg-slate-800/50 rounded-3xl border border-slate-700 overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex border-b border-slate-700">
              <button className="flex-1 py-4 text-center font-medium bg-teal-500/10 border-b-2 border-teal-500 text-teal-400">
                {t('landing.leaderboard.global')}
              </button>
              <button className="flex-1 py-4 text-center font-medium text-slate-400 hover:text-white transition-colors">
                {t('landing.leaderboard.myGroups')}
              </button>
            </div>

            <div className="p-6 space-y-3">
              {[
                { rank: 1, name: 'Alice', score: '98%', time: '3:10', highlight: false },
                { rank: 2, name: 'Mark', score: '98%', time: '3:21', highlight: false },
                { rank: 3, name: t('landing.leaderboard.you'), score: '94%', time: '3:18', highlight: true },
                { rank: 4, name: 'Sarah', score: '92%', time: '3:45', highlight: false },
                { rank: 5, name: 'John', score: '90%', time: '3:32', highlight: false }
              ].map((entry, i) => (
                <motion.div 
                  key={i}
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    entry.highlight 
                      ? 'bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-500/30' 
                      : 'bg-slate-800/30'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      entry.rank === 1 ? 'bg-yellow-500 text-yellow-900' :
                      entry.rank === 2 ? 'bg-slate-400 text-slate-900' :
                      entry.rank === 3 ? 'bg-orange-500 text-orange-900' :
                      'bg-slate-700 text-slate-300'
                    }`}>
                      {entry.rank}
                    </div>
                    <span className={`font-medium ${entry.highlight ? 'text-teal-400' : ''}`}>
                      {entry.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="text-teal-400 font-bold">{entry.score}</span>
                    <span className="text-slate-500 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {entry.time}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Groups Section */}
      <section id="groups" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.groupsSection.title')} <span className="text-teal-400">{t('landing.groupsSection.titleHighlight')}</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {t('landing.groupsSection.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div className="p-8 bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-3xl border border-teal-500/20 text-center" whileHover={{ y: -5 }}>
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-teal-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('landing.groupsSection.createGroup')}</h3>
              <p className="text-slate-400 text-sm">{t('landing.groupsSection.createDesc')}</p>
            </motion.div>

            <motion.div className="p-8 bg-slate-800/30 rounded-3xl border border-slate-700 text-center" whileHover={{ y: -5 }}>
              <div className="w-16 h-16 bg-slate-700/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔗</span>
              </div>
              <h3 className="text-xl font-bold mb-2">{t('landing.groupsSection.shareCode')}</h3>
              <p className="text-slate-400 text-sm">{t('landing.groupsSection.shareDesc')}</p>
            </motion.div>

            <motion.div className="p-8 bg-slate-800/30 rounded-3xl border border-slate-700 text-center" whileHover={{ y: -5 }}>
              <div className="w-16 h-16 bg-slate-700/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('landing.groupsSection.compete')}</h3>
              <p className="text-slate-400 text-sm">{t('landing.groupsSection.competeDesc')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative py-24 px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.testimonials.title')} <span className="text-teal-400">{t('landing.testimonials.titleHighlight')}</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <motion.div 
                key={i}
                className="p-6 bg-slate-800/30 rounded-2xl border border-slate-700"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-4">"{testimonial.text}"</p>
                <div>
                  <p className="font-medium">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('landing.faqSection.title')} <span className="text-teal-400">{t('landing.faqSection.titleHighlight')}</span>
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div 
                key={i}
                className="bg-slate-800/30 rounded-2xl border border-slate-700 overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <button 
                  className="w-full p-6 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <motion.div 
                    className="px-6 pb-6"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                  >
                    <p className="text-slate-400">{faq.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {t('landing.cta.title')} <span className="text-teal-400">{t('landing.cta.titleHighlight')}</span>?
            </h2>
            <p className="text-slate-400 text-xl mb-8">
              {t('landing.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={handlePlayNow}
                className="group relative px-10 py-5 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl font-bold text-xl shadow-2xl shadow-teal-500/30 hover:shadow-teal-500/50 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10 flex items-center justify-center gap-3">
                  <Play className="w-6 h-6 fill-current" />
                  {t('landing.cta.playBrowser')}
                </span>
              </motion.button>
              
              {showAppDownload && (
              <motion.button
                onClick={handleDownloadAndroid}
                className="group relative px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl font-bold text-xl shadow-2xl shadow-green-500/30 hover:shadow-green-500/50 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                animate={{ 
                  boxShadow: ['0 25px 50px -12px rgba(34, 197, 94, 0.3)', '0 25px 50px -12px rgba(34, 197, 94, 0.6)', '0 25px 50px -12px rgba(34, 197, 94, 0.3)']
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="relative z-10 flex items-center justify-center gap-3">
                  <AndroidIcon className="w-6 h-6" />
                  {t('landing.cta.downloadAndroid')}
                </span>
              </motion.button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-6 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full shadow-lg shadow-teal-500/30 overflow-hidden border-2 border-teal-400/30">
                <img src={LOGO_URL} alt="SocraQuest Logo" className="w-full h-full object-cover" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                SocraQuest
              </span>
            </div>
            
            <p className="text-slate-500 text-sm">
              {t('landing.footer.copyright')}
            </p>

            <div className="flex items-center gap-6">
              <a href="/privacy-policy" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">{t('landing.footer.privacy')}</a>
              <a href="/terms-of-service" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">{t('landing.footer.terms')}</a>
              <a href="mailto:support@socraquest.sk" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">{t('landing.footer.contact')}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating CTA Button (Mobile, website only) */}
      {showAppDownload && (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 md:hidden z-50">
        <motion.button
          onClick={handleDownloadAndroid}
          className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full font-bold shadow-2xl shadow-green-500/40 flex items-center gap-2"
          whileTap={{ scale: 0.95 }}
          animate={{ 
            scale: [1, 1.05, 1],
            boxShadow: ['0 25px 50px -12px rgba(34, 197, 94, 0.4)', '0 25px 50px -12px rgba(34, 197, 94, 0.7)', '0 25px 50px -12px rgba(34, 197, 94, 0.4)']
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <AndroidIcon className="w-5 h-5" />
          {t('landing.nav.downloadApp')}
        </motion.button>
      </div>
      )}
    </div>
  );
}

export default LandingPage;
